import { CloseIcon } from '@blocksuite/icons/lit';
import { safeCalendarExternalUrl } from '@streamlain/calendar-ics-security';
import { format } from 'date-fns/format';
import { html, nothing, type TemplateResult } from 'lit';

import {
  dataViewI18nT,
  getDataViewRuntimeLocale,
} from '../../../../core/i18n.js';
import {
  type CalendarOccurrence,
  parseCalendarAttendees,
  validateCalendarRecurrenceRule,
} from '../../calendar-domain.js';
import type { CalendarSingleView } from '../../calendar-view-manager.js';
import { styles } from '../calendar-view-styles.js';
import type { CalendarEvent } from '../calendar-view-types.js';
import { formatAttendeeToken, guessTimeZone } from '../calendar-view-utils.js';
import {
  formatExceptionDates,
  formatRecurrenceForm,
  formatReminderForm,
  parseExceptionDates,
  parseRecurrenceForm,
  parseReminderForm,
  type RecurrenceFormValue,
  type ReminderFormValue,
} from './calendar-detail-values.js';

type SetCellJson = (
  rowId: string,
  propertyId: string | undefined,
  value: unknown
) => void;

export function formatCalendarDetailDate(date: Date): string {
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
    dateStyle: 'medium',
  }).format(date);
}

export function formatCalendarDetailDateTime(date: Date): string {
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

type DetailEditorContext = {
  event: CalendarEvent;
  readonly: boolean;
  rowId: string;
  setCellJson: SetCellJson;
  view: CalendarSingleView;
};

export function renderAttendeesEditor({
  event,
  readonly,
  rowId,
  setCellJson,
  view,
}: DetailEditorContext): TemplateResult {
  const attendees = parseCalendarAttendees(event.attendees);
  const setAttendees = (value: string) =>
    setCellJson(rowId, view.attendeesCol$.value, value);
  return html`<div class="${styles.field}">
    <span>${dataViewI18nT('calendar.detail.attendees')}</span>
    <div class="${styles.chipList}">
      ${attendees.length
        ? attendees.map(
            attendee =>
              html`<button
                class="${styles.attendeeChip}"
                data-attendee-status=${attendee.status}
                type="button"
                ?disabled=${readonly}
                aria-label=${dataViewI18nT(
                  'calendar.accessibility.removeAttendee',
                  {
                    name: attendee.name || attendee.email || attendee.key,
                  }
                )}
                @click=${() => {
                  const next = attendees
                    .filter(item => item.key !== attendee.key)
                    .map(formatAttendeeToken)
                    .join(', ');
                  setAttendees(next);
                }}
              >
                ${attendee.name || attendee.email || attendee.key}
                ${attendee.status !== 'needs_action'
                  ? html`<small>${attendee.status}</small>`
                  : nothing}
              </button>`
          )
        : html`<small>${dataViewI18nT('calendar.detail.noAttendees')}</small>`}
    </div>
    <div class="${styles.inlineEditorRow}">
      <input
        type="text"
        ?disabled=${readonly}
        placeholder=${dataViewI18nT('calendar.detail.nameEmailPlaceholder')}
        @keydown=${(key: KeyboardEvent) => {
          if (key.key !== 'Enter') return;
          key.preventDefault();
          const input = key.currentTarget as HTMLInputElement;
          const value = input.value.trim();
          if (!value) return;
          setAttendees([event.attendees, value].filter(Boolean).join(', '));
          input.value = '';
        }}
      />
      <button
        class="${styles.todayButton}"
        type="button"
        ?disabled=${readonly}
        @click=${(click: Event) => {
          const row = (click.currentTarget as HTMLElement).parentElement;
          const input = row?.querySelector('input');
          const value = input?.value.trim() ?? '';
          if (!input || !value) return;
          setAttendees([event.attendees, value].filter(Boolean).join(', '));
          input.value = '';
        }}
      >
        ${dataViewI18nT('calendar.action.add')}
      </button>
    </div>
  </div>`;
}

export function renderReminderEditor({
  event,
  readonly,
  rowId,
  setCellJson,
  view,
}: DetailEditorContext): TemplateResult {
  const form = parseReminderForm(event.reminder);
  const update = (patch: Partial<ReminderFormValue>) => {
    setCellJson(
      rowId,
      view.reminderCol$.value,
      formatReminderForm({ ...form, ...patch })
    );
  };
  return html`<div class="${styles.field}">
    <span>${dataViewI18nT('calendar.detail.reminder')}</span>
    <div class="${styles.inlineEditorRow}">
      <input
        type="number"
        min="1"
        ?disabled=${readonly}
        .value=${form.amount}
        @change=${(change: Event) =>
          update({
            amount: (change.currentTarget as HTMLInputElement).value,
          })}
      />
      <select
        .value=${form.unit}
        ?disabled=${readonly}
        @change=${(change: Event) =>
          update({
            unit: (change.currentTarget as HTMLSelectElement)
              .value as ReminderFormValue['unit'],
          })}
      >
        <option value="m">
          ${dataViewI18nT('calendar.detail.minutesBefore')}
        </option>
        <option value="h">
          ${dataViewI18nT('calendar.detail.hoursBefore')}
        </option>
        <option value="d">
          ${dataViewI18nT('calendar.detail.daysBefore')}
        </option>
      </select>
    </div>
  </div>`;
}

function renderRecurrenceFrequencySelect(
  form: RecurrenceFormValue,
  readonly: boolean,
  update: (patch: Partial<RecurrenceFormValue>) => void
): TemplateResult {
  return html`<select
    .value=${form.frequency}
    ?disabled=${readonly}
    @change=${(change: Event) =>
      update({
        frequency: (change.currentTarget as HTMLSelectElement)
          .value as RecurrenceFormValue['frequency'],
      })}
  >
    <option value="">${dataViewI18nT('calendar.detail.doesNotRepeat')}</option>
    <option value="DAILY">
      ${dataViewI18nT('calendar.detail.recurrenceDaily')}
    </option>
    <option value="WEEKLY">
      ${dataViewI18nT('calendar.detail.recurrenceWeekly')}
    </option>
    <option value="MONTHLY">
      ${dataViewI18nT('calendar.detail.recurrenceMonthly')}
    </option>
    <option value="YEARLY">
      ${dataViewI18nT('calendar.detail.recurrenceYearly')}
    </option>
  </select>`;
}

function renderRecurrenceNumberInput({
  ariaLabel,
  disabled,
  onChange,
  placeholder,
  value,
}: {
  ariaLabel: string;
  disabled: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  value: string;
}): TemplateResult {
  return html`<input
    type="number"
    min="1"
    aria-label=${ariaLabel}
    placeholder=${placeholder ?? ''}
    .value=${value}
    ?disabled=${disabled}
    @change=${(change: Event) =>
      onChange((change.currentTarget as HTMLInputElement).value)}
  />`;
}

function renderRecurrenceUntilInput(
  form: RecurrenceFormValue,
  readonly: boolean,
  update: (patch: Partial<RecurrenceFormValue>) => void
): TemplateResult {
  return html`<input
    type="date"
    aria-label=${dataViewI18nT('calendar.accessibility.recurrenceUntil')}
    .value=${form.until}
    ?disabled=${readonly || !form.frequency || Boolean(form.count)}
    @change=${(change: Event) =>
      update({
        until: (change.currentTarget as HTMLInputElement).value,
      })}
  />`;
}

export function renderRecurrenceEditor(
  context: DetailEditorContext & {
    setCalendarError: (message: string | undefined) => void;
  }
): TemplateResult {
  const { event, rowId, setCalendarError, setCellJson, view } = context;
  const form = parseRecurrenceForm(event.recurrence);
  const update = (patch: Partial<RecurrenceFormValue>) => {
    const next = formatRecurrenceForm({ ...form, ...patch });
    try {
      validateCalendarRecurrenceRule(next);
    } catch (error) {
      setCalendarError(
        error instanceof Error
          ? error.message
          : dataViewI18nT('calendar.grid.invalidRecurrence')
      );
      return;
    }
    setCalendarError(undefined);
    setCellJson(rowId, view.recurrenceCol$.value, next);
  };
  return html`<div class="${styles.field} ${styles.sectionDivider}">
    <span>${dataViewI18nT('calendar.detail.recurrence')}</span>
    <div class="${styles.recurrenceGrid}">
      <div class="${styles.recurrenceField}">
        <span
          >${dataViewI18nT('calendar.detail.recurrenceFrequencyLabel')}</span
        >
        ${renderRecurrenceFrequencySelect(form, context.readonly, update)}
      </div>
      <div class="${styles.recurrenceField}">
        <span>${dataViewI18nT('calendar.detail.recurrenceEveryLabel')}</span>
        ${renderRecurrenceNumberInput({
          ariaLabel: dataViewI18nT('calendar.accessibility.recurrenceInterval'),
          disabled: context.readonly || !form.frequency,
          onChange: value => update({ interval: value }),
          value: form.interval,
        })}
      </div>
      <div class="${styles.recurrenceField}">
        <span>${dataViewI18nT('calendar.detail.count')}</span>
        ${renderRecurrenceNumberInput({
          ariaLabel: dataViewI18nT('calendar.accessibility.recurrenceCount'),
          disabled: context.readonly || !form.frequency,
          onChange: value => update({ count: value }),
          placeholder: dataViewI18nT('calendar.detail.count'),
          value: form.count,
        })}
      </div>
      <div class="${styles.recurrenceField}">
        <span>${dataViewI18nT('calendar.detail.recurrenceUntilLabel')}</span>
        ${renderRecurrenceUntilInput(form, context.readonly, update)}
      </div>
    </div>
  </div>`;
}

export function renderExceptionsEditor({
  event,
  readonly,
  rowId,
  setCellJson,
  view,
}: DetailEditorContext): TemplateResult {
  const values = parseExceptionDates(event.recurrenceExdate);
  const write = (next: readonly string[]) =>
    setCellJson(
      rowId,
      view.recurrenceExdateCol$.value,
      formatExceptionDates(next)
    );
  return html`<div class="${styles.field}">
    <span>${dataViewI18nT('calendar.detail.exceptions')}</span>
    <div class="${styles.chipList}">
      ${values.length
        ? values.map(
            value =>
              html`<button
                class="${styles.attendeeChip}"
                type="button"
                ?disabled=${readonly}
                aria-label=${dataViewI18nT(
                  'calendar.accessibility.removeException',
                  {
                    date: formatCalendarDetailDate(new Date(value)),
                  }
                )}
                @click=${() => write(values.filter(item => item !== value))}
              >
                ${formatCalendarDetailDate(new Date(value))}
              </button>`
          )
        : html`<small
            >${dataViewI18nT('calendar.detail.noSkippedDates')}</small
          >`}
    </div>
    <div class="${styles.inlineEditorRow}">
      <input type="date" ?disabled=${readonly} />
      <button
        class="${styles.todayButton}"
        type="button"
        ?disabled=${readonly}
        @click=${(click: Event) => {
          const row = (click.currentTarget as HTMLElement).parentElement;
          const input = row?.querySelector('input');
          if (!input?.value) return;
          write([...values, new Date(input.value).toISOString()]);
          input.value = '';
        }}
      >
        ${dataViewI18nT('calendar.action.add')}
      </button>
    </div>
  </div>`;
}

export function renderReadonlyCalendarDetails({
  event,
  occurrence,
  onClose,
}: {
  event: CalendarEvent;
  occurrence: CalendarOccurrence<CalendarEvent> | undefined;
  onClose: () => void;
}): TemplateResult {
  const startAt = occurrence?.startAt ?? event.startAt;
  const endAt = occurrence?.endAt ?? event.endAt;
  return html`<aside class="${styles.details}">
    <div class="${styles.detailsHeader}">
      <strong>${event.title}</strong>
      <button
        class="${styles.iconButton}"
        aria-label=${dataViewI18nT('calendar.accessibility.closeDetails')}
        @click=${onClose}
      >
        ${CloseIcon({ height: '14px', width: '14px' })}
      </button>
    </div>
    <div class="${styles.detailsBody}" data-calendar-detail-scroll>
      <div class="${styles.notice}">
        ${dataViewI18nT('calendar.detail.readonlyFeedEvent')}
      </div>
      <dl class="${styles.readonlyDetails}">
        <dt>${dataViewI18nT('calendar.detail.time')}</dt>
        <dd>
          ${event.allDay
            ? dataViewI18nT('calendar.detail.allDay')
            : `${formatCalendarDetailDateTime(
                startAt
              )} - ${formatCalendarDetailDateTime(endAt)}`}
        </dd>
        ${event.locationPhysical
          ? html`<dt>${dataViewI18nT('calendar.detail.address')}</dt>
              <dd>${event.locationPhysical}</dd>`
          : nothing}
        ${safeCalendarExternalUrl(event.locationVirtual)
          ? html`<dt>${dataViewI18nT('calendar.detail.virtualLink')}</dt>
              <dd>
                <a
                  href=${safeCalendarExternalUrl(event.locationVirtual)}
                  target="_blank"
                  rel="noreferrer"
                >
                  ${event.locationVirtual}
                </a>
              </dd>`
          : nothing}
        ${event.attendees
          ? html`<dt>${dataViewI18nT('calendar.detail.attendees')}</dt>
              <dd>${event.attendees}</dd>`
          : nothing}
        ${event.description
          ? html`<dt>${dataViewI18nT('calendar.detail.description')}</dt>
              <dd>${event.description}</dd>`
          : nothing}
      </dl>
    </div>
  </aside>`;
}

export function renderCalendarInput(
  label: string,
  value: string,
  onChange: (value: string) => void,
  type = 'text',
  readonly = false,
  onCommit: () => void = () => {}
): TemplateResult {
  return html`<label class="${styles.field}">
    <span>${label}</span>
    <input
      type=${type}
      ?disabled=${readonly}
      .value=${value}
      @input=${(event: InputEvent) =>
        onChange((event.currentTarget as HTMLInputElement).value)}
      @keydown=${(event: KeyboardEvent) => {
        if (event.key === 'Tab') onCommit();
      }}
      @blur=${onCommit}
    />
  </label>`;
}

export function renderCalendarTextarea(
  label: string,
  value: string,
  onChange: (value: string) => void,
  readonly = false,
  onCommit: () => void = () => {}
): TemplateResult {
  return html`<label class="${styles.field}">
    <span>${label}</span>
    <textarea
      .value=${value}
      ?disabled=${readonly}
      @input=${(event: InputEvent) =>
        onChange((event.currentTarget as HTMLTextAreaElement).value)}
      @keydown=${(event: KeyboardEvent) => {
        if (event.key === 'Tab') onCommit();
      }}
      @blur=${onCommit}
    ></textarea>
  </label>`;
}

export function renderCalendarSelect(
  label: string,
  value: string,
  options: ReadonlyArray<readonly [string, string]>,
  onChange: (value: string) => void,
  readonly = false
): TemplateResult {
  return html`<label class="${styles.field}">
    <span>${label}</span>
    <select
      .value=${value}
      ?disabled=${readonly}
      @change=${(change: Event) =>
        onChange((change.currentTarget as HTMLSelectElement).value)}
    >
      ${options.map(
        ([optionValue, optionLabel]) =>
          html`<option value=${optionValue}>${optionLabel}</option>`
      )}
    </select>
  </label>`;
}

export function renderCalendarDateInput(
  label: string,
  value: Date,
  onChange: (value: Date) => void,
  readonly = false
): TemplateResult {
  return html`<label class="${styles.field}">
    <span>${label}</span>
    <input
      type="datetime-local"
      .value=${format(value, "yyyy-MM-dd'T'HH:mm")}
      ?disabled=${readonly}
      @change=${(change: Event) => {
        const input = change.currentTarget as HTMLInputElement;
        const next = new Date(input.value);
        if (Number.isFinite(next.getTime())) onChange(next);
      }}
    />
  </label>`;
}

export function renderTimezoneInput(
  value: string,
  onChange: (value: string) => void,
  readonly = false,
  onCommit: () => void = () => {}
): TemplateResult {
  return renderCalendarInput(
    dataViewI18nT('calendar.detail.timezone'),
    value || guessTimeZone(),
    onChange,
    'text',
    readonly,
    onCommit
  );
}
